import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon,
  BoltIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ClockIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const BlockchainWalletPage: React.FC = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <CubeTransparentIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">Blockchain Solutions</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Enterprise Digital Wallet <span className="text-emerald-300">Infrastructure</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              Eliminate per-transaction fees, enable instant settlements, and maintain complete control over your token-based payment system—without exposing users to cryptocurrency complexity.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/schedule-meeting"
                className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Schedule a Demo</span>
              </Link>
              <a
                href="#use-cases"
                className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
              >
                <span>Explore Use Cases</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Blockchain Wallet?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on private blockchain technology for enterprise-grade security with payment card simplicity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                <CurrencyDollarIcon className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Zero Fees</h3>
              <p className="text-gray-600">No transaction costs for internal transfers—unlimited frequency</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <BoltIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Instant</h3>
              <p className="text-gray-600">Settlement in seconds, not days—real-time finality</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <ShieldCheckIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Secure</h3>
              <p className="text-gray-600">Private blockchain with enterprise-grade encryption</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <LockClosedIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Private</h3>
              <p className="text-gray-600">Your infrastructure, your rules—complete control</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Core Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <CubeTransparentIcon className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Private Blockchain Infrastructure</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Hyperledger Besu (Enterprise Ethereum)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>ERC-20 compliant token standard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Proof of Authority consensus</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Multi-node redundancy</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <BanknotesIcon className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Stablecoin Token Economics</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>1:1 peg with fiat currency</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No volatility or exchange rate risk</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>On-demand token minting</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Transparent supply management</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <ClockIcon className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Zero-Fee Internal Transactions</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No transaction costs internally</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Instant settlement within seconds</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unlimited transaction frequency</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Complete blockchain audit trail</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <svg className="w-12 h-12 text-orange-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Payment Gateway Integration</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Seamless fiat-to-token conversion</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Multiple payment methods</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>One-time PIN verification</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time balance sync</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <ShieldCheckIcon className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Security</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Private blockchain (no public access)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Encrypted wallet keys in secure vault</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Multi-signature requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Fraud detection algorithms</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <svg className="w-12 h-12 text-teal-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-3">User Experience</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No crypto knowledge required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Familiar payment experience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Clear fiat currency display</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>SMS notifications</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Perfect for businesses that need cost-effective, instant digital payments
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Gaming & Rewards",
                icon: "🎮",
                description: "Zero-fee prize distributions and in-app purchases with instant settlement",
                benefits: ["No transaction fees", "Instant prize payouts", "Immediate reuse of funds"]
              },
              {
                title: "Corporate Token Economy",
                icon: "🏢",
                description: "Internal company currency for rewards, benefits, and services",
                benefits: ["Employee incentives", "Internal marketplace", "Expense reimbursement"]
              },
              {
                title: "Loyalty Programs",
                icon: "⭐",
                description: "Token-based loyalty with real monetary value and instant redemption",
                benefits: ["Real-value points", "Instant redemption", "Partner network"]
              },
              {
                title: "Remittance & Transfers",
                icon: "💸",
                description: "Instant money transfers with minimal fees and real-time settlement",
                benefits: ["Low fees", "Instant transfers", "Complete transparency"]
              },
              {
                title: "Event Credits",
                icon: "🎫",
                description: "Prepaid event credits with easy refunds and transferability",
                benefits: ["Token-based credits", "Easy refunds", "Transferable"]
              },
              {
                title: "Charitable Platforms",
                icon: "❤️",
                description: "Zero-fee donations mean 100% of funds reach beneficiaries",
                benefits: ["Zero fees", "Transparent tracking", "Instant distribution"]
              }
            ].map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-200 hover:border-emerald-500 transition-colors"
              >
                <div className="text-4xl mb-4">{useCase.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                <p className="text-gray-700 mb-4">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <CheckCircleIcon className="w-4 h-4 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Target Industries
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Gaming & Entertainment", icon: "🎮" },
              { name: "Retail & E-Commerce", icon: "🛍️" },
              { name: "Financial Services", icon: "💰" },
              { name: "Corporate Enterprise", icon: "🏢" },
              { name: "Events & Hospitality", icon: "🎉" },
              { name: "Education & Training", icon: "🎓" },
              { name: "Healthcare", icon: "🏥" },
              { name: "Non-Profit & Social Impact", icon: "❤️" }
            ].map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
              >
                <div className="text-4xl mb-3">{industry.icon}</div>
                <h3 className="text-lg font-bold text-gray-900">{industry.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Integrates with QuickReach AI Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Standalone blockchain solution that can enhance your AI capabilities
            </p>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white p-12 rounded-2xl">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h3 className="text-xl font-bold mb-2">+ DocFlow AI</h3>
                <p className="text-emerald-100">AI-verified documents trigger automated token transfers</p>
              </div>
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="text-xl font-bold mb-2">+ DB Talker</h3>
                <p className="text-blue-100">Query wallet transactions in natural language</p>
              </div>
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-xl font-bold mb-2">+ Analytics</h3>
                <p className="text-purple-100">Real-time wallet and transaction dashboards</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="text-lg font-bold mb-4">Example Integration Workflow:</h4>
              <ol className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-white text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                  <span>DocFlow AI processes and verifies supplier invoice</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                  <span>Smart contract automatically transfers tokens to supplier wallet</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                  <span>DB Talker queries: "Show me all payments to this supplier this quarter"</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                  <span>Analytics dashboard displays real-time payment metrics</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Blockchain Wallet?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">vs. Public Blockchain Wallets</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Private Infrastructure:</span> No public blockchain exposure
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">No Crypto Complexity:</span> Users don't need crypto knowledge
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Fiat-Pegged:</span> No volatility or exchange rate risk
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Zero Fees:</span> No gas fees for transactions
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">vs. Traditional Payment Processors</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Zero Transaction Fees:</span> For internal transfers
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Instant Settlement:</span> No T+1 or T+2 delays
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Complete Audit Trail:</span> Blockchain immutability
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Programmable:</span> Smart contract automation
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo" className="py-20 bg-gradient-to-r from-emerald-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Eliminate Transaction Fees?
          </h2>
          <p className="text-xl mb-8">
            See how Blockchain Wallet can transform your payment infrastructure
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Schedule a Demo</span>
            </Link>
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-emerald-600 transition-all"
            >
              <span>Request Proof of Concept</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlockchainWalletPage;

